import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { useGetOrganizationsQuery } from '../../api/organizationApi';
import { useCreateOrgUserMutation } from '../../api/peopleApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import useAutoSelectFirst from '../../hooks/useAutoSelectFirst';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import buttons from '../../styles/shared/buttons.module.css';

const ROLES = [
  { label: 'manager', value: 'manager' },
  { label: 'host', value: 'host' },
];

export default function UserCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();

  const { data: organizations, isLoading: loadingOrgs } = useGetOrganizationsQuery();
  const [createUser, { isLoading: isCreating }] = useCreateOrgUserMutation();

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      organizationId: location.state?.organizationId ?? '',
      role: 'manager',
      name: '',
      email: '',
      password: '',
    },
  });
  const organizationId = watch('organizationId');

  useAutoSelectFirst(organizations, organizationId, (id) => setValue('organizationId', id));

  const onCreate = async (data) => {
    try {
      await createUser({
        organizationId: data.organizationId,
        role: data.role,
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password.trim(),
      }).unwrap();
      showToast('success', 'Users', `${data.role === 'manager' ? 'Manager' : 'Host'} created.`);
      navigate('/admin/users');
    } catch (err) {
      showToast(
        'error',
        'Users',
        err?.status === 409
          ? 'A user with that email already exists.'
          : errMsg(err, 'Could not create user.'),
      );
    }
  };

  if (loadingOrgs) return <p className="textMuted">Loading…</p>;

  return (
    <EntityFormPage title="Add user" backTo="/admin/users">
      <form onSubmit={handleSubmit(onCreate)}>
        <Controller
          name="organizationId"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Organization</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={organizations?.map((c) => ({ label: `${c.name} (${c.slug})`, value: c.id }))}
                className="w-full mb-3"
              />
            </div>
          )}
        />
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Role</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={ROLES}
                className="w-full mb-3"
              />
            </div>
          )}
        />
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Name</label>
              <InputText id={field.name} className="w-full mb-3" {...field} />
            </div>
          )}
        />
        <Controller
          name="email"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Email</label>
              <InputText id={field.name} type="email" className="w-full mb-3" {...field} />
            </div>
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={{ required: true, minLength: 8 }}
          render={({ field, fieldState }) => (
            <div className="field">
              <label htmlFor={field.name}>Password (min 8 chars)</label>
              <Password
                inputId={field.name}
                className={`w-full ${fieldState.invalid ? 'p-invalid' : ''}`}
                inputClassName="w-full"
                toggleMask
                feedback={false}
                {...field}
              />
            </div>
          )}
        />
        <Button
          type="submit"
          label="Save"
          className={buttons.saveBtn}
          loading={isCreating}
          disabled={!organizationId}
        />
      </form>
    </EntityFormPage>
  );
}
